import { type Static, type TSchema, Type } from '@sinclair/typebox';

const StripeRefOrObject = <T extends TSchema>(type: T) => Type.Union([Type.String(), Type.Null(), type]);
const Nullable = <T extends TSchema>(schema: T) => Type.Union([schema, Type.Null()]);
const NullableOrUndefined = <T extends TSchema>(schema: T) => Type.Optional(Type.Union([schema, Type.Null()]));

export const StripeApiListDTOSchema = <T extends TSchema>(item: T) =>
  Type.Object({
    data: Type.Array(item),
    has_more: Type.Boolean(),
  });

// =====================================================================================================================
// Product
// =====================================================================================================================

export const StripeProductDTOSchema = Type.Object({
  id: Type.String(),
  metadata: Type.Optional(Type.Record(Type.String(), Type.String())),
  name: Type.Optional(Type.String()),
});
export type StripeProductDTO = Static<typeof StripeProductDTOSchema>;

// =====================================================================================================================
// Price
// =====================================================================================================================

/**
 * Metadata are not returned, they contain the amount of the platformFee, and the frontend does not need to access it
 */
export const StripePriceDTOSchema = Type.Object({
  currency: Type.String(),
  id: Type.String(),
  product: StripeRefOrObject(StripeProductDTOSchema),
  unit_amount: Type.Union([Type.Number(), Type.Null()]),
});
export type StripePriceDTO = Static<typeof StripePriceDTOSchema>;

// =====================================================================================================================
// Account
// =====================================================================================================================

export const StripeAccountDTOSchema = Type.Object({
  id: Type.String(),
  details_submitted: Type.Boolean(),
  default_currency: Type.Optional(Type.String()),
  requirements: NullableOrUndefined(
    Type.Object({
      eventually_due: Nullable(Type.Array(Type.String())),
    }),
  ),
  settings: NullableOrUndefined(
    Type.Object({
      payouts: Type.Optional(
        Type.Object({
          schedule: Type.Object({
            interval: Type.String(),
            monthly_anchor: Type.Optional(Type.Number()),
          }),
        }),
      ),
    }),
  ),
});
export type StripeAccountDTO = Static<typeof StripeAccountDTOSchema>;

// =====================================================================================================================
// Payment Method
// =====================================================================================================================

export const StripePaymentMethodDTOSchema = Type.Object({
  id: Type.String(),
  type: Type.String(),
  card: Type.Optional(
    Type.Object({
      brand: Type.String(),
      exp_year: Type.Number(),
      exp_month: Type.Number(),
      last4: Type.String(),
    }),
  ),
});

// =====================================================================================================================
// Invoice without dependencies
// =====================================================================================================================

const StripeInvoiceDTOBaseSchema = Type.Object({
  billing_reason: Nullable(
    Type.Union([
      Type.Literal('automatic_pending_invoice_item_invoice'),
      Type.Literal('quote_accept'),
      Type.Literal('subscription'),
      Type.Literal('manual'),
      Type.Literal('subscription_create'),
      Type.Literal('subscription_cycle'),
      Type.Literal('subscription_threshold'),
      Type.Literal('subscription_update'),
      Type.Literal('upcoming'),
    ]),
  ),
  id: Type.String(),
  hosted_invoice_url: Type.Optional(Nullable(Type.String())),
  invoice_pdf: NullableOrUndefined(Type.String()),
  next_payment_attempt: Nullable(Type.Number()),
  period_end: Type.Number(),
  status: Nullable(
    Type.Union([
      Type.Literal('draft'),
      Type.Literal('open'),
      Type.Literal('paid'),
      Type.Literal('uncollectible'),
      Type.Literal('void'),
    ]),
  ),
});

// =====================================================================================================================
// Subscription builder
// =====================================================================================================================

export const StripeSubscriptionItemDTOSchema = Type.Object({
  id: Type.String(),
  price: StripePriceDTOSchema,
});

export const StripeSubscriptionDTOSchema = Type.Object({
  id: Type.String(),
  cancel_at_period_end: Type.Boolean(),
  currency: Type.String(),
  current_period_end: Type.Number(),
  default_payment_method: StripeRefOrObject(StripePaymentMethodDTOSchema),
  items: Type.Object({
    data: Type.Array(StripeSubscriptionItemDTOSchema),
  }),
  latest_invoice: StripeRefOrObject(StripeInvoiceDTOBaseSchema),
  pending_update: Nullable(
    Type.Object({
      expires_at: Type.Number(),
      subscription_items: Nullable(Type.Array(StripeSubscriptionItemDTOSchema)),
    }),
  ),
  schedule: StripeRefOrObject(
    Type.Object({
      current_phase: StripeRefOrObject(
        Type.Object({
          start_date: Type.Number(),
          end_date: Type.Number(),
        }),
      ),
    }),
  ),
  status: Type.Union([
    Type.Literal('active'),
    Type.Literal('canceled'),
    Type.Literal('incomplete'),
    Type.Literal('incomplete_expired'),
    Type.Literal('past_due'),
    Type.Literal('paused'),
    Type.Literal('trialing'),
    Type.Literal('unpaid'),
  ]),
});
export type StripeSubscriptionDTO = Static<typeof StripeSubscriptionDTOSchema>;

// =====================================================================================================================
// Invoice
// =====================================================================================================================

export const StripeInvoiceDTOSchema = Type.Composite([
  StripeInvoiceDTOBaseSchema,
  Type.Object({ subscription: StripeRefOrObject(StripeSubscriptionDTOSchema) }),
]);
export type StripeInvoiceDTO = Static<typeof StripeInvoiceDTOSchema>;

export const StripeInvoiceLineItemDTOSchema = Type.Object({
  amount: Type.Number(),
  currency: Type.String(),
  price: Nullable(StripePriceDTOSchema),
  tax_amounts: Type.Array(
    Type.Object({
      amount: Type.Number(),
    }),
  ),
});
export type StripeInvoiceLineItemDTO = Static<typeof StripeInvoiceLineItemDTOSchema>;

// =====================================================================================================================
// Payment Intent
// =====================================================================================================================

export const StripePaymentIntentDTOSchema = Type.Object({
  amount: Type.Number(),
  created: Type.Number(),
  id: Type.String(),
  currency: Type.String(),
  invoice: StripeRefOrObject(StripeInvoiceDTOSchema),
  next_action: Nullable(
    Type.Object({
      redirect_to_url: Type.Optional(
        Type.Object({
          url: NullableOrUndefined(Type.String()),
          return_url: NullableOrUndefined(Type.String()),
        }),
      ),
    }),
  ),
  status: Type.Union([
    Type.Literal('canceled'),
    Type.Literal('processing'),
    Type.Literal('requires_action'),
    Type.Literal('requires_capture'),
    Type.Literal('requires_confirmation'),
    Type.Literal('requires_payment_method'),
    Type.Literal('succeeded'),
  ]),
});

export type StripePaymentIntentDTO = Static<typeof StripePaymentIntentDTOSchema>;

// =====================================================================================================================
// Customer
// =====================================================================================================================

export const StripeCustomerDTOSchema = Type.Object({
  id: Type.String(),
  currency: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  payment_methods: Type.Array(StripePaymentMethodDTOSchema),
  invoice_settings: Type.Object({
    default_payment_method: StripeRefOrObject(StripePaymentMethodDTOSchema),
  }),
  subscriptions: Type.Optional(StripeApiListDTOSchema(StripeSubscriptionDTOSchema)),
});
export type StripeCustomerDTO = Static<typeof StripeCustomerDTOSchema>;

// =====================================================================================================================
// Line Item
// =====================================================================================================================

export const StripeLineItemDTOSchema = Type.Object({
  amount_tax: Type.Number(),
  amount_total: Type.Number(),
  currency: Type.String(),
  price: Nullable(StripePriceDTOSchema),
});
export type StripeLineItemDTO = Static<typeof StripeLineItemDTOSchema>;
